import ExperienceEditorLink from '../../GalleryExperienceEditor/ExperienceEditorLink';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useGallery from '@/src/GalleryConfiguration/components/hooks/useGallery';
import { useEventContext } from '../components/EventContext';
import { photosUploaded } from '../lib/eventStats';
import { Switch } from '@/src/shadComponents/ui/switch';
import AssetPicker from '../components/AssetPicker';
import useCrowdGallery from '@/src/GalleryConfiguration/components/hooks/useCrowdGallery';
import useSlideshows, {
    ActivationTag,
    GallerySlideshow,
} from '@/src/GalleryConfiguration/components/hooks/useSlideshows';
import useModerationToken from '@/src/GalleryConfiguration/components/hooks/useModerationToken';
import useFTPSettings from '@/src/GalleryConfiguration/components/hooks/useFTPSettings';
import usePicTime from '@/src/Orgs/components/hooks/usePicTime';

/*
 * Gallery v2 — the gallery hub (subject-detail archetype): left pane is
 * the gallery's identity (name, stats, links), right pane its settings
 * (basics, appearance, forwarding, tracking) saved as one object via
 * the classic PUT /galleries/:id — plus separately-saved sections
 * (crowd gallery, FTP sync, tag-scoped slideshows). Asset uploads stay
 * in classic for now — this page links there for replacements.
 */

// Mirrors cloud/modules/mediaTags/service so chips match what the server stores.
function normalizeTag(tag: string): string {
    return tag.trim().replace(/\s+/g, ' ').toLowerCase();
}

const IconExternal = ({ className = 'h-[15px] w-[15px]' }) => (
    <svg
        viewBox="0 0 20 20"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M8 4H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3M12 4h4v4M16 4l-7 7" />
    </svg>
);

const IconRevoke = () => (
    <svg
        viewBox="0 0 20 20"
        className="h-[13px] w-[13px]"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="10" cy="10" r="7" />
        <path d="m6.5 6.5 7 7" />
    </svg>
);

const IconCopy = () => (
    <svg
        viewBox="0 0 20 20"
        className="h-[13px] w-[13px]"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="7" y="7" width="9" height="9" rx="2" />
        <path d="M13 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    </svg>
);

interface GalleryLink {
    title: string;
    path: string;
}

const GALLERY_LINKS: GalleryLink[] = [
    { title: 'Public gallery', path: '' },
    { title: 'Sharing station', path: '/sharing' },
    { title: 'Slideshow', path: '/slideshow' },
];

export default function GalleryV2() {
    const navigate = useNavigate();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const { getGallery, saveGallery } = useGallery();
    const {
        gallery: ctxGallery,
        domain: ctxDomain,
        resolved,
        ensureShares,
        ensureMedia,
        refreshGallery,
    } = useEventContext();
    const { getCrowdGallery, saveCrowdGallery } = useCrowdGallery();
    const {
        getSlideshows,
        createSlideshow,
        updateSlideshow,
        deleteSlideshow,
        getActivationTags,
    } = useSlideshows();
    const { getFTPSettings, saveFTPSettings, saveFTPSettingsWithPassword } =
        useFTPSettings();
    const { isLinkedToPictime } = usePicTime();
    const {
        getModerationToken,
        createModerationToken,
        revokeModerationToken,
        getModerationHold,
        setModerationHold,
    } = useModerationToken();

    const [gallery, setGallery] = useState<any>(null);
    /* moderation magic link — stored token so Revoke works */
    const [modToken, setModToken] = useState<string | null>(null);
    const [modBusy, setModBusy] = useState(false);
    /* gallery intake mode (mock: mocks/moderation-link.html) */
    const [modHold, setModHold] = useState(false);
    const [modHoldBusy, setModHoldBusy] = useState(false);
    const [form, setForm] = useState<any>(null);
    const [stats, setStats] = useState({ views: 0, shares: 0, photos: 0 });
    const [galleryUrl, setGalleryUrl] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [savedAt, setSavedAt] = useState<number | null>(null);
    /* "Saved just now" decays to plain "Saved" */
    useEffect(() => {
        if (!savedAt) return;
        const timer = setTimeout(() => setSavedAt(null), 10 * 1000);
        return () => clearTimeout(timer);
    }, [savedAt]);
    const [saveError, setSaveError] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);
    const [assetPicker, setAssetPicker] = useState<string | null>(null);
    const [pictimeLinked, setPictimeLinked] = useState(false);
    const [crowd, setCrowd] = useState<any>(null);
    const [crowdForm, setCrowdForm] = useState<any>(null);
    const [crowdSnapshot, setCrowdSnapshot] = useState('');
    const [crowdSaving, setCrowdSaving] = useState(false);
    const [ftpForm, setFtpForm] = useState<any>(null);
    const [ftpSnapshot, setFtpSnapshot] = useState('');
    const [ftpSaving, setFtpSaving] = useState(false);
    const [sectionError, setSectionError] = useState<string | null>(null);
    const [slideshows, setSlideshows] = useState<GallerySlideshow[]>([]);
    const [activationTags, setActivationTags] = useState<ActivationTag[]>([]);
    /* null = closed, 0 = creating, otherwise the slideshow id being edited */
    const [ssEditing, setSsEditing] = useState<number | null>(null);
    const [ssForm, setSsForm] = useState<{
        name: string;
        tags: string[];
        matchMode: 'any' | 'all';
    } | null>(null);
    const [ssDraft, setSsDraft] = useState('');
    const [ssBusy, setSsBusy] = useState(false);
    const [tfDraft, setTfDraft] = useState('');

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [shares, mediaRows] = await Promise.all([
                    ensureShares(),
                    ensureMedia(),
                ]);
                if (!mounted) return;
                setGalleryUrl(ctxDomain);
                /* canonical Photos — see lib/eventStats */
                const photos = photosUploaded(mediaRows);
                const shareIds = new Set(shares.map((s: any) => s.id));

                const first = ctxGallery;
                if (first) {
                    const [
                        full,
                        crowdRow,
                        ftpRow,
                        linked,
                        ssRows,
                        actTags,
                        modRow,
                        hold,
                    ] =
                        await Promise.all([
                            getGallery(first.id),
                            getCrowdGallery(first.id).catch(() => null),
                            getFTPSettings(first.id).catch(() => null),
                            isLinkedToPictime().catch(() => false),
                            getSlideshows(first.id).catch(() => []),
                            getActivationTags(id).catch(() => []),
                            getModerationToken(first.id),
                            getModerationHold(first.id),
                        ]);
                    if (!mounted) return;
                    setGallery(full);
                    setModToken(modRow?.token ?? null);
                    setModHold(hold);
                    setForm(fromGallery(full));
                    setPictimeLinked(!!linked);
                    setSlideshows(ssRows ?? []);
                    setActivationTags(actTags ?? []);
                    if (crowdRow) {
                        setCrowd(crowdRow);
                        const cf = {
                            enabled: !!crowdRow.enabled,
                            keyword: crowdRow.keyword ?? '',
                            welcomeMessage: crowdRow.welcomeMessage ?? '',
                            responseMessage: crowdRow.responseMessage ?? '',
                            webResponseMessage:
                                crowdRow.webResponseMessage ?? '',
                            moderationEnabled: !!crowdRow.moderationEnabled,
                            moderationNumber:
                                crowdRow.moderationNumber ?? '',
                            sendPhoto: !!crowdRow.sendPhoto,
                            sendPersonalGalleryLink:
                                !!crowdRow.sendPersonalGalleryLink,
                            sendGalleryLink: !!crowdRow.sendGalleryLink,
                        };
                        setCrowdForm(cf);
                        setCrowdSnapshot(JSON.stringify(cf));
                    }
                    if (ftpRow) {
                        const ff = {
                            ftpHost: ftpRow.ftpHost ?? '',
                            ftpPort: ftpRow.ftpPort ?? '',
                            ftpUser: ftpRow.ftpUser ?? '',
                            ftpDirectory: ftpRow.ftpDirectory ?? '',
                            ftpPassword: '',
                        };
                        setFtpForm(ff);
                        setFtpSnapshot(JSON.stringify(ff));
                    }
                    setStats({
                        views: Number(first.totalViews) || 0,
                        shares: shareIds.size,
                        photos,
                    });
                } else {
                    setStats({ views: 0, shares: shareIds.size, photos });
                }
            } catch (e) {
                /* empty state handles it */
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        if (resolved) load();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, resolved]);

    const fromGallery = (g: any) => ({
        name: g.name ?? '',
        slug: g.slug ?? '',
        isPrivate: !!g.isPrivate,
        password: g.password ?? '',
        headerBackgroundColor: g.headerBackgroundColor ?? '',
        headerForegroundColor: g.headerForegroundColor ?? '',
        backgroundColor: g.backgroundColor ?? '',
        foregroundColor: g.foregroundColor ?? '',
        logo: g.logo ?? '',
        ctaImage: g.ctaImage ?? '',
        backgroundImage: g.backgroundImage ?? '',
        ctaText: g.ctaText ?? '',
        ctaLink: g.ctaLink ?? '',
        labelSearchPrompt: g.labelSearchPrompt ?? '',
        syncWithPictime: !!g.syncWithPictime,
        mfEnable: !!g.galleryMediaFilter?.enable,
        mfOriginal: !!g.galleryMediaFilter?.original,
        mfPhotos: !!g.galleryMediaFilter?.photos,
        mfVideos: !!g.galleryMediaFilter?.videos,
        mfEdited: !!g.galleryMediaFilter?.edited,
        mfPrints: !!g.galleryMediaFilter?.prints,
        tfEnable: !!g.galleryTagFilter?.enable,
        tfMode: g.galleryTagFilter?.mode === 'exclude' ? 'exclude' : 'include',
        tfTags: g.galleryTagFilter?.tags ?? [],
        tfMatch: g.galleryTagFilter?.matchMode === 'all' ? 'all' : 'any',
        forwardingEnabled: !!(g.galleryForwardUrl || g.shareForwardUrl),
        galleryForwardUrl: g.galleryForwardUrl ?? '',
        shareForwardUrl: g.shareForwardUrl ?? '',
        metaPixelId: g.metaPixelId ?? '',
    });

    const dirty = useMemo(() => {
        if (!gallery || !form) return false;
        return (
            JSON.stringify(form) !== JSON.stringify(fromGallery(gallery))
        );
    }, [gallery, form]);

    const set = (key: string, value: any) => {
        setForm((f: any) => ({ ...f, [key]: value }));
        setSaveError(false);
    };

    const save = async () => {
        if (!gallery || !form || isSaving) return;
        setIsSaving(true);
        setSaveError(false);
        const payload = {
            ...gallery,
            name: form.name,
            slug: form.slug,
            isPrivate: form.isPrivate,
            password: form.isPrivate ? form.password : '',
            headerBackgroundColor: form.headerBackgroundColor,
            headerForegroundColor: form.headerForegroundColor,
            backgroundColor: form.backgroundColor,
            foregroundColor: form.foregroundColor,
            logo: form.logo || null,
            ctaImage: form.ctaImage || null,
            backgroundImage: form.backgroundImage || null,
            ctaText: form.ctaText,
            ctaLink: form.ctaLink,
            labelSearchPrompt: form.labelSearchPrompt || null,
            syncWithPictime: form.syncWithPictime,
            galleryMediaFilter: {
                enable: form.mfEnable,
                original: form.mfOriginal,
                photos: form.mfPhotos,
                videos: form.mfVideos,
                edited: form.mfEdited,
                prints: form.mfPrints,
            },
            galleryTagFilter: {
                enable: form.tfEnable,
                mode: form.tfMode,
                tags: form.tfTags,
                matchMode: form.tfMatch,
            },
            galleryForwardUrl: form.forwardingEnabled
                ? form.galleryForwardUrl || null
                : null,
            shareForwardUrl: form.forwardingEnabled
                ? form.shareForwardUrl || null
                : null,
            metaPixelId: form.metaPixelId,
        };
        const ok = await saveGallery(gallery.id, payload).catch(() => false);
        setIsSaving(false);
        if (ok) {
            setGallery(payload);
            setForm(fromGallery(payload));
            setSavedAt(Date.now());
            refreshGallery();
        } else {
            setSaveError(true);
        }
    };

    const copy = (key: string, text: string) => {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(key);
            setTimeout(() => setCopied(null), 1500);
        });
    };

    const crowdDirty =
        crowdForm != null && JSON.stringify(crowdForm) !== crowdSnapshot;
    const ftpDirty =
        ftpForm != null && JSON.stringify(ftpForm) !== ftpSnapshot;

    const saveCrowd = async () => {
        if (!crowd || !crowdForm || crowdSaving) return;
        setCrowdSaving(true);
        setSectionError(null);
        const ok = await saveCrowdGallery(crowd.id, {
            ...crowdForm,
            galleryId: gallery.id,
        }).catch(() => false);
        setCrowdSaving(false);
        if (ok) {
            setCrowdSnapshot(JSON.stringify(crowdForm));
        } else {
            setSectionError('crowd');
        }
    };

    /* fold the tag-filter draft into the form (Enter or blur, so an
       un-entered tag isn't silently lost when the user hits Save) */
    const pushTfTag = () => {
        const t = normalizeTag(tfDraft);
        setTfDraft('');
        if (!t || !form || form.tfTags.includes(t)) return;
        set('tfTags', [...form.tfTags, t]);
    };

    const startSlideshowEdit = (s?: GallerySlideshow) => {
        setSsEditing(s?.id ?? 0);
        setSsForm({
            name: s?.name ?? '',
            tags: s?.tags ?? [],
            matchMode: s?.matchMode ?? 'any',
        });
        setSsDraft('');
        setSectionError(null);
    };

    const closeSlideshowEdit = () => {
        setSsEditing(null);
        setSsForm(null);
        setSsDraft('');
    };

    const pushSsTag = () => {
        const t = normalizeTag(ssDraft);
        setSsDraft('');
        if (!t || !ssForm || ssForm.tags.includes(t)) return;
        setSsForm({ ...ssForm, tags: [...ssForm.tags, t] });
    };

    const saveSlideshow = async () => {
        if (!ssForm || ssBusy || !gallery || ssEditing == null) return;
        const name = ssForm.name.trim();
        if (!name) return;
        /* fold an un-entered draft tag in rather than losing it */
        const draft = normalizeTag(ssDraft);
        const tags =
            draft && !ssForm.tags.includes(draft)
                ? [...ssForm.tags, draft]
                : ssForm.tags;
        setSsBusy(true);
        setSectionError(null);
        const payload = { name, tags, matchMode: ssForm.matchMode };
        const saved = ssEditing
            ? await updateSlideshow(ssEditing, payload)
            : await createSlideshow(gallery.id, payload);
        setSsBusy(false);
        if (!saved) {
            setSectionError('slideshows');
            return;
        }
        setSlideshows((rows) =>
            ssEditing
                ? rows.map((r) => (r.id === saved.id ? saved : r))
                : [...rows, saved],
        );
        closeSlideshowEdit();
    };

    const removeSlideshow = async (s: GallerySlideshow) => {
        if (!window.confirm(`Delete slideshow “${s.name}”?`)) return;
        const ok = await deleteSlideshow(s.id);
        if (ok) {
            setSlideshows((rows) => rows.filter((r) => r.id !== s.id));
            if (ssEditing === s.id) closeSlideshowEdit();
        } else {
            setSectionError('slideshows');
        }
    };

    const saveFtp = async () => {
        if (!ftpForm || ftpSaving) return;
        setFtpSaving(true);
        setSectionError(null);
        const payload = { ...ftpForm, galleryId: gallery.id };
        const ok = await (ftpForm.ftpPassword
            ? saveFTPSettingsWithPassword(gallery.id, payload)
            : saveFTPSettings(gallery.id, payload)
        ).catch(() => false);
        setFtpSaving(false);
        if (ok) {
            setFtpSnapshot(
                JSON.stringify({ ...ftpForm, ftpPassword: '' }),
            );
            setFtpForm((f: any) => ({ ...f, ftpPassword: '' }));
        } else {
            setSectionError('ftp');
        }
    };

    const publicBase = gallery?.slug
        ? `${galleryUrl}/gallery/${gallery.slug}`
        : '';
    const displayUrl = (u: string) => u.replace(/^https?:\/\//, '');

    const moderationUrl =
        gallery?.slug && modToken
            ? `${galleryUrl}/moderation/${gallery.slug}/${modToken}`
            : '';
    const createModLink = async () => {
        if (!gallery || modBusy) return;
        setModBusy(true);
        const row = await createModerationToken(gallery.id);
        setModBusy(false);
        if (row?.token) setModToken(row.token);
    };
    const revokeModLink = async () => {
        if (!modToken || modBusy) return;
        setModBusy(true);
        const ok = await revokeModerationToken(modToken);
        setModBusy(false);
        if (ok) setModToken(null);
    };
    const setIntakeMode = async (hold: boolean) => {
        if (!gallery || modHold === hold || modHoldBusy) return;
        setModHoldBusy(true);
        setSectionError(null);
        const ok = await setModerationHold(gallery.id, hold);
        setModHoldBusy(false);
        if (ok) {
            setModHold(hold);
        } else {
            setSectionError('modhold');
        }
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                    />
                ))}
            </main>
        );
    }

    if (!gallery) {
        return (
            <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="ps-label mb-4">Gallery</div>
                <p className="text-[13.5px] text-muted-foreground">
                    This activation has no gallery yet.{' '}
                    <button
                        type="button"
                        onClick={() => navigate(`/activations/${id}/galleries`)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] font-semibold text-brand-ink"
                    >
                        Create one in the classic app →
                    </button>
                </p>
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[1020px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-[18px]">Gallery</div>
            <ExperienceEditorLink
                activationId={activationId}
                galleryId={gallery?.id}
            />
            <div className="grid gap-0 md:grid-cols-[352px_1fr]">
                {/* LEFT — the gallery hub */}
                <div className="md:pr-10">
                    <div className="mb-[22px] flex items-center gap-2.5">
                        <h1 className="text-[23px] font-bold leading-tight tracking-[-0.02em]">
                            {form.name || 'Untitled gallery'}
                        </h1>
                        {publicBase && (
                            <a
                                href={publicBase}
                                target="_blank"
                                rel="noreferrer"
                                title="View gallery"
                                className="flex h-7 w-7 items-center justify-center rounded-[8px] text-muted-foreground hover:bg-secondary hover:text-foreground"
                            >
                                <IconExternal />
                            </a>
                        )}
                    </div>

                    <div className="mb-1.5 flex gap-6">
                        {[
                            {
                                n: stats.photos,
                                l: 'Photos',
                                dot: 'bg-brand',
                            },
                            {
                                n: stats.shares,
                                l: 'Shares',
                                dot: 'bg-attention',
                            },
                            {
                                n: stats.views,
                                l: 'Views',
                                dot: 'bg-faint',
                            },
                        ].map((s) => (
                            <div key={s.l}>
                                <div className="text-[21px] font-bold tracking-[-0.01em] tabular-nums">
                                    {s.n.toLocaleString()}
                                </div>
                                <div className="mt-px flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                                    <span
                                        className={`h-1.5 w-1.5 rounded-full ${s.dot}`}
                                    />
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate(`/v2/activations/${id}/insights`)}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        View insights →
                    </button>

                    <div className="mt-7">
                        <div className="ps-label mb-1.5">Gallery links</div>
                        {GALLERY_LINKS.map((link, li) => {
                            const href = publicBase
                                ? `${publicBase}${link.path}`
                                : '';
                            return (
                                <div
                                    key={link.title}
                                    className={
                                        'flex items-center gap-2.5 py-[9px] ' +
                                        (li > 0 ? 'border-t border-line' : '')
                                    }
                                >
                                    <div className="min-w-0">
                                        <div className="text-[12.5px] font-semibold leading-tight">
                                            {link.title}
                                        </div>
                                        <div className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[10.5px] text-faint">
                                            {href ? displayUrl(href) : '—'}
                                        </div>
                                    </div>
                                    {href && (
                                        <div className="ml-auto flex gap-0.5">
                                            <button
                                                type="button"
                                                title={
                                                    copied === link.title
                                                        ? 'Copied'
                                                        : 'Copy'
                                                }
                                                onClick={() =>
                                                    copy(link.title, href)
                                                }
                                                className={
                                                    'flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent hover:bg-secondary hover:text-foreground ' +
                                                    (copied === link.title
                                                        ? 'text-good'
                                                        : 'text-faint')
                                                }
                                            >
                                                <IconCopy />
                                            </button>
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Open"
                                                className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-faint hover:bg-secondary hover:text-foreground"
                                            >
                                                <IconExternal className="h-[13px] w-[13px]" />
                                            </a>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Moderation magic link — the one row with
                            write power, so it alone carries a grant
                            line and a revoke (mock:
                            mocks/moderation-link.html) */}
                        {gallery && (
                            <div className="flex items-center gap-2.5 border-t border-line py-[9px]">
                                <div className="min-w-0">
                                    <div className="text-[12.5px] font-semibold leading-tight">
                                        Moderation
                                    </div>
                                    {moderationUrl ? (
                                        <>
                                            <div className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[10.5px] text-faint">
                                                {displayUrl(moderationUrl)}
                                            </div>
                                            <div className="text-[10.5px] text-faint">
                                                Anyone with this link can
                                                approve & remove photos
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-[10.5px] text-faint">
                                            Magic link — approve & remove
                                            photos, no login
                                        </div>
                                    )}
                                </div>
                                {moderationUrl ? (
                                    <div className="ml-auto flex gap-0.5">
                                        <button
                                            type="button"
                                            title={
                                                copied === 'Moderation'
                                                    ? 'Copied'
                                                    : 'Copy'
                                            }
                                            onClick={() =>
                                                copy(
                                                    'Moderation',
                                                    moderationUrl,
                                                )
                                            }
                                            className={
                                                'flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent hover:bg-secondary hover:text-foreground ' +
                                                (copied === 'Moderation'
                                                    ? 'text-good'
                                                    : 'text-faint')
                                            }
                                        >
                                            <IconCopy />
                                        </button>
                                        <a
                                            href={moderationUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            title="Open"
                                            className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-faint hover:bg-secondary hover:text-foreground"
                                        >
                                            <IconExternal className="h-[13px] w-[13px]" />
                                        </a>
                                        <button
                                            type="button"
                                            title="Revoke link"
                                            onClick={revokeModLink}
                                            disabled={modBusy}
                                            className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent text-faint hover:bg-secondary hover:text-destructive"
                                        >
                                            <IconRevoke />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={createModLink}
                                        disabled={modBusy}
                                        className="ml-auto flex-none cursor-pointer rounded-full border border-border bg-transparent px-3 py-[5px] text-[11.5px] font-semibold text-foreground hover:bg-secondary"
                                    >
                                        {modBusy
                                            ? 'Creating…'
                                            : 'Create link'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT — settings */}
                <div className="mt-10 md:mt-0 md:border-l md:border-line md:pl-10">
                    <div className="mb-1.5 flex h-7 items-center justify-end gap-3">
                        {saveError && (
                            <span className="text-[11.5px] font-semibold text-attention">
                                Save failed — try again
                            </span>
                        )}
                        {dirty ? (
                            <button
                                type="button"
                                onClick={save}
                                disabled={isSaving}
                                className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                            >
                                {isSaving ? 'Saving…' : 'Save changes'}
                            </button>
                        ) : (
                            <span className="text-[11.5px] text-faint">
                                {savedAt ? 'Saved just now' : 'Saved'}
                            </span>
                        )}
                    </div>

                    {/* settings */}
                    <section className="pb-6 pt-3.5">
                        <h3 className="ps-label mb-[15px]">Settings</h3>
                        <div className="mb-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Gallery name
                            </label>
                            <input
                                className="ps-input w-full"
                                value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                            />
                        </div>
                        <div className="mb-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Public address
                            </label>
                            <div className="flex items-baseline border-b-[1.5px] border-border focus-within:border-brand">
                                <span className="py-[9px] font-mono text-[12px] text-faint">
                                    {displayUrl(galleryUrl)}/gallery/
                                </span>
                                <input
                                    className="flex-1 border-0 bg-transparent py-[9px] font-mono text-[12.5px] text-foreground outline-none"
                                    value={form.slug}
                                    onChange={(e) =>
                                        set('slug', e.target.value)
                                    }
                                />
                            </div>
                        </div>
                        <div>
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Privacy
                            </label>
                            <div className="inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                                {[
                                    { label: 'Public', value: false },
                                    { label: 'Password', value: true },
                                ].map((o) => (
                                    <button
                                        key={o.label}
                                        type="button"
                                        onClick={() =>
                                            set('isPrivate', o.value)
                                        }
                                        className={
                                            'cursor-pointer rounded-[7px] border-0 px-[13px] py-[5px] font-[inherit] text-[12.5px] font-semibold ' +
                                            (form.isPrivate === o.value
                                                ? 'bg-background text-foreground'
                                                : 'bg-transparent text-muted-foreground')
                                        }
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {form.isPrivate && (
                                <div className="mt-[15px]">
                                    <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        className="ps-input w-full"
                                        value={form.password}
                                        onChange={(e) =>
                                            set('password', e.target.value)
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </section>

                    {/* appearance */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-1.5">Appearance</h3>
                        <p className="mb-4 text-[12.5px] text-muted-foreground">
                            How the gallery looks to guests.
                        </p>
                        <div className="mb-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Colors
                            </label>
                            <div className="flex flex-wrap gap-5">
                                {[
                                    {
                                        key: 'headerBackgroundColor',
                                        label: 'Header',
                                    },
                                    {
                                        key: 'headerForegroundColor',
                                        label: 'Header text',
                                    },
                                    {
                                        key: 'backgroundColor',
                                        label: 'Background',
                                    },
                                    {
                                        key: 'foregroundColor',
                                        label: 'Text',
                                    },
                                ].map((c) => (
                                    <div
                                        key={c.key}
                                        className="flex items-center gap-2"
                                    >
                                        <span
                                            className="h-6 w-6 rounded-[7px] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]"
                                            style={{
                                                background:
                                                    form[c.key] || '#ffffff',
                                            }}
                                        />
                                        <span>
                                            <span className="block text-[12px] font-semibold">
                                                {c.label}
                                            </span>
                                            <input
                                                className="w-[76px] border-0 bg-transparent p-0 font-mono text-[10.5px] uppercase text-faint outline-none"
                                                value={form[c.key]}
                                                placeholder="#——"
                                                onChange={(e) =>
                                                    set(c.key, e.target.value)
                                                }
                                            />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Logo, call-to-action & background
                            </label>
                            <div className="flex flex-wrap gap-[22px]">
                                {[
                                    { key: 'logo', label: 'Logo' },
                                    { key: 'ctaImage', label: 'CTA' },
                                    {
                                        key: 'backgroundImage',
                                        label: 'Background',
                                    },
                                ].map((a) => (
                                    <div key={a.key}>
                                        <div className="flex h-14 w-[82px] items-center justify-center overflow-hidden rounded-[9px] bg-secondary">
                                            {form[a.key] ? (
                                                <img
                                                    src={form[a.key]}
                                                    alt={a.label}
                                                    className="h-full w-full object-contain"
                                                />
                                            ) : (
                                                <span className="text-[10px] text-faint">
                                                    None
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1.5 text-[11px] text-muted-foreground">
                                            {a.label} ·{' '}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setAssetPicker(a.key)
                                                }
                                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-brand-ink"
                                            >
                                                Replace
                                            </button>
                                            {form[a.key] && (
                                                <>
                                                    {' · '}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            set(a.key, '')
                                                        }
                                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11px] font-semibold text-faint hover:text-attention"
                                                    >
                                                        Remove
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 max-w-[520px] text-[11px] leading-relaxed text-faint">
                                Background is optional: it fills the whole page
                                behind the header and gallery and replaces the
                                background colors while set. Use a landscape
                                image at least 1920×1080, JPG or WebP under
                                1 MB, with key detail centered — edges crop on
                                phones.
                            </p>
                        </div>
                    </section>

                    {/* forwarding */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-[15px]">Forwarding</h3>
                        <div className="flex items-start justify-between gap-3.5">
                            <div>
                                <div className="text-[14px] font-semibold">
                                    Forward visitors to a custom URL
                                </div>
                                <div className="mt-1 max-w-[42ch] text-[12.5px] text-muted-foreground">
                                    Send gallery and share-link visitors to
                                    your own site instead.
                                </div>
                            </div>
                            <Switch
                                checked={form.forwardingEnabled}
                                onCheckedChange={(v) =>
                                    set('forwardingEnabled', v)
                                }
                            />
                        </div>
                        {form.forwardingEnabled && (
                            <>
                                <div className="mt-[15px]">
                                    <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                        Gallery URL
                                    </label>
                                    <input
                                        className="ps-input w-full font-mono text-[13px]"
                                        placeholder="https://"
                                        value={form.galleryForwardUrl}
                                        onChange={(e) =>
                                            set(
                                                'galleryForwardUrl',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                                <div className="mt-[15px]">
                                    <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                        Share-link URL{' '}
                                        <span className="font-normal text-faint">
                                            — optional
                                        </span>
                                    </label>
                                    <input
                                        className="ps-input w-full font-mono text-[13px]"
                                        placeholder="Defaults to the gallery URL"
                                        value={form.shareForwardUrl}
                                        onChange={(e) =>
                                            set(
                                                'shareForwardUrl',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </section>

                    {/* tracking */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-[15px]">Tracking</h3>
                        <div>
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Meta Pixel ID
                            </label>
                            <input
                                className="ps-input w-full font-mono text-[13px]"
                                value={form.metaPixelId}
                                onChange={(e) =>
                                    set('metaPixelId', e.target.value)
                                }
                            />
                        </div>
                    </section>

                    {/* content — what guests see in the gallery */}
                    <section className="border-t border-line py-6">
                        <h3 className="ps-label mb-[15px]">Content</h3>
                        <div className="flex items-start justify-between gap-3.5">
                            <div>
                                <div className="text-[14px] font-semibold">
                                    Filter what appears in the gallery
                                </div>
                                <div className="mt-1 max-w-[42ch] text-[12.5px] text-muted-foreground">
                                    When off, everything captured shows.
                                </div>
                            </div>
                            <Switch
                                checked={form.mfEnable}
                                onCheckedChange={(v) => set('mfEnable', v)}
                            />
                        </div>
                        {form.mfEnable && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {[
                                    { k: 'mfPhotos', l: 'Photos' },
                                    { k: 'mfVideos', l: 'Videos' },
                                    { k: 'mfEdited', l: 'Edited' },
                                    { k: 'mfPrints', l: 'Prints' },
                                    { k: 'mfOriginal', l: 'Originals' },
                                ].map((c) => (
                                    <button
                                        key={c.k}
                                        type="button"
                                        onClick={() => set(c.k, !form[c.k])}
                                        className={
                                            'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                            (form[c.k]
                                                ? 'border border-transparent bg-brand-soft text-brand-ink'
                                                : 'border border-border bg-transparent text-muted-foreground')
                                        }
                                    >
                                        {c.l}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="mt-[18px] flex items-start justify-between gap-3.5">
                            <div>
                                <div className="text-[14px] font-semibold">
                                    Filter by tag
                                </div>
                                <div className="mt-1 max-w-[42ch] text-[12.5px] text-muted-foreground">
                                    Only show — or hide — media carrying
                                    specific tags. Applies everywhere the
                                    gallery's photos appear.
                                </div>
                            </div>
                            <Switch
                                checked={form.tfEnable}
                                onCheckedChange={(v) => set('tfEnable', v)}
                            />
                        </div>
                        {form.tfEnable && (
                            <div className="mt-3">
                                <div className="mb-2.5 inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                                    {[
                                        {
                                            l: 'Only show tagged',
                                            v: 'include' as const,
                                        },
                                        {
                                            l: 'Hide tagged',
                                            v: 'exclude' as const,
                                        },
                                    ].map((o) => (
                                        <button
                                            key={o.v}
                                            type="button"
                                            onClick={() =>
                                                set('tfMode', o.v)
                                            }
                                            className={
                                                'cursor-pointer rounded-[7px] border-0 px-3 py-1 font-[inherit] text-[12px] font-semibold ' +
                                                (form.tfMode === o.v
                                                    ? 'bg-background text-foreground'
                                                    : 'bg-transparent text-muted-foreground')
                                            }
                                        >
                                            {o.l}
                                        </button>
                                    ))}
                                </div>
                                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                                    {form.tfTags.map((t: string) => (
                                        <span
                                            key={t}
                                            className="inline-flex items-center gap-1 rounded-full bg-brand-soft py-[3px] pl-2.5 pr-1 text-[12px] font-semibold text-brand-ink"
                                        >
                                            {t}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    set(
                                                        'tfTags',
                                                        form.tfTags.filter(
                                                            (x: string) =>
                                                                x !== t,
                                                        ),
                                                    )
                                                }
                                                className="cursor-pointer border-0 bg-transparent px-1 text-faint hover:text-attention"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        className="ps-input w-[140px] text-[12.5px]"
                                        placeholder="Type a tag, Enter"
                                        value={tfDraft}
                                        onChange={(e) =>
                                            setTfDraft(e.target.value)
                                        }
                                        onBlur={pushTfTag}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                pushTfTag();
                                            }
                                        }}
                                    />
                                </div>
                                {activationTags.filter(
                                    (t) => !form.tfTags.includes(t.tag),
                                ).length > 0 && (
                                    <div className="mb-2.5 flex flex-wrap items-center gap-1">
                                        {activationTags
                                            .filter(
                                                (t) =>
                                                    !form.tfTags.includes(
                                                        t.tag,
                                                    ),
                                            )
                                            .slice(0, 12)
                                            .map((t) => (
                                                <button
                                                    key={t.tag}
                                                    type="button"
                                                    onClick={() =>
                                                        set('tfTags', [
                                                            ...form.tfTags,
                                                            t.tag,
                                                        ])
                                                    }
                                                    className="cursor-pointer rounded-full border border-dashed border-border bg-transparent px-2.5 py-[3px] font-[inherit] text-[11.5px] text-muted-foreground hover:text-brand-ink"
                                                >
                                                    + {t.tag}
                                                    <span className="ml-1 tabular-nums text-faint">
                                                        {t.count}
                                                    </span>
                                                </button>
                                            ))}
                                    </div>
                                )}
                                {form.tfTags.length > 1 && (
                                    <div className="flex items-center gap-2.5">
                                        <span className="text-[12px] font-semibold text-muted-foreground">
                                            {form.tfMode === 'exclude'
                                                ? 'Hide media having'
                                                : 'Show media having'}
                                        </span>
                                        <div className="inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                                            {[
                                                {
                                                    l: 'Any tag',
                                                    v: 'any' as const,
                                                },
                                                {
                                                    l: 'All tags',
                                                    v: 'all' as const,
                                                },
                                            ].map((o) => (
                                                <button
                                                    key={o.v}
                                                    type="button"
                                                    onClick={() =>
                                                        set('tfMatch', o.v)
                                                    }
                                                    className={
                                                        'cursor-pointer rounded-[7px] border-0 px-3 py-1 font-[inherit] text-[12px] font-semibold ' +
                                                        (form.tfMatch === o.v
                                                            ? 'bg-background text-foreground'
                                                            : 'bg-transparent text-muted-foreground')
                                                    }
                                                >
                                                    {o.l}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="mt-[15px]">
                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                Label search prompt{' '}
                                <span className="font-normal text-faint">
                                    — optional
                                </span>
                            </label>
                            <input
                                className="ps-input w-full"
                                value={form.labelSearchPrompt}
                                onChange={(e) =>
                                    set('labelSearchPrompt', e.target.value)
                                }
                            />
                        </div>
                        {pictimeLinked && (
                            <div className="mt-[15px] flex items-center justify-between gap-3.5">
                                <span className="text-[14px] font-semibold">
                                    Sync with Pic-Time
                                </span>
                                <Switch
                                    checked={form.syncWithPictime}
                                    onCheckedChange={(v) =>
                                        set('syncWithPictime', v)
                                    }
                                />
                            </div>
                        )}
                    </section>

                    {/* intake mode — its own section, saved on click
                        (mock: mocks/moderation-link.html) */}
                    <section className="border-t border-line py-6">
                        <div className="mb-[3px] flex h-7 items-center justify-between">
                            <h3 className="ps-label">Moderation</h3>
                            {sectionError === 'modhold' && (
                                <span className="text-[11.5px] font-semibold text-attention">
                                    Save failed — try again
                                </span>
                            )}
                        </div>
                        {(
                            [
                                {
                                    hold: false,
                                    title: 'New photos appear immediately',
                                    sub: 'Every capture and upload goes straight to the gallery, slideshow, and sharing. Whoever holds the moderation link can remove photos after the fact.',
                                },
                                {
                                    hold: true,
                                    title: 'Hold new photos for approval',
                                    sub: 'Nothing shows publicly until someone approves it from the moderation link. Applies to every source — booth, uploads, texts, and web.',
                                },
                            ] as const
                        ).map((opt, oi) => (
                            <button
                                key={opt.title}
                                type="button"
                                onClick={() => setIntakeMode(opt.hold)}
                                disabled={modHoldBusy}
                                className={
                                    'flex w-full cursor-pointer items-start gap-[11px] border-0 bg-transparent p-0 py-[11px] text-left font-[inherit]' +
                                    (oi > 0 ? ' border-t border-line' : '')
                                }
                            >
                                <span
                                    className={
                                        'relative mt-px h-4 w-4 flex-none rounded-full border-[1.5px] ' +
                                        (modHold === opt.hold
                                            ? 'border-brand'
                                            : 'border-off')
                                    }
                                >
                                    {modHold === opt.hold && (
                                        <span className="absolute inset-[3px] rounded-full bg-brand" />
                                    )}
                                </span>
                                <span>
                                    <span className="block text-[13px] font-semibold leading-[1.3]">
                                        {opt.title}
                                    </span>
                                    <span className="mt-0.5 block max-w-[52ch] text-[11.5px] leading-[1.45] text-muted-foreground">
                                        {opt.sub}
                                    </span>
                                </span>
                            </button>
                        ))}
                        {modHold && (
                            <div className="mt-1.5 flex items-center gap-2 rounded-[9px] border border-border bg-sunk px-3 py-[9px] text-[11.5px] text-muted-foreground">
                                <span className="h-[7px] w-[7px] flex-none rounded-full bg-attention" />
                                New photos are held — hand the moderation
                                link to whoever approves.
                            </div>
                        )}
                    </section>

                    {/* crowd gallery — separate save */}
                    {crowdForm && (
                        <section className="border-t border-line py-6">
                            <div className="mb-[15px] flex h-7 items-center justify-between">
                                <h3 className="ps-label">Crowd gallery</h3>
                                <span className="flex items-center gap-3">
                                    {sectionError === 'crowd' && (
                                        <span className="text-[11.5px] font-semibold text-attention">
                                            Save failed
                                        </span>
                                    )}
                                    {crowdDirty && (
                                        <button
                                            type="button"
                                            onClick={saveCrowd}
                                            disabled={crowdSaving}
                                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                                        >
                                            {crowdSaving
                                                ? 'Saving…'
                                                : 'Save changes'}
                                        </button>
                                    )}
                                </span>
                            </div>
                            <div className="flex items-start justify-between gap-3.5">
                                <div>
                                    <div className="text-[14px] font-semibold">
                                        Guests text photos in
                                    </div>
                                    <div className="mt-1 max-w-[42ch] text-[12.5px] text-muted-foreground">
                                        SMS keyword drops guest photos into
                                        the gallery.
                                    </div>
                                </div>
                                <Switch
                                    checked={crowdForm.enabled}
                                    onCheckedChange={(v) =>
                                        setCrowdForm((f: any) => ({
                                            ...f,
                                            enabled: v,
                                        }))
                                    }
                                />
                            </div>
                            {crowdForm.enabled && (
                                <>
                                    {[
                                        { k: 'keyword', l: 'Keyword', mono: true },
                                        {
                                            k: 'welcomeMessage',
                                            l: 'Welcome message',
                                        },
                                        {
                                            k: 'responseMessage',
                                            l: 'Response message',
                                        },
                                        {
                                            k: 'webResponseMessage',
                                            l: 'Web response message',
                                        },
                                    ].map((f2) => (
                                        <div key={f2.k} className="mt-[15px]">
                                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                                {f2.l}
                                            </label>
                                            <input
                                                className={
                                                    'ps-input w-full' +
                                                    (f2.mono
                                                        ? ' font-mono text-[13px]'
                                                        : '')
                                                }
                                                value={crowdForm[f2.k]}
                                                onChange={(e) =>
                                                    setCrowdForm(
                                                        (f: any) => ({
                                                            ...f,
                                                            [f2.k]:
                                                                e.target
                                                                    .value,
                                                        }),
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                    <div className="mt-3.5 flex flex-wrap gap-1.5">
                                        {[
                                            { k: 'sendPhoto', l: 'Send photo back' },
                                            {
                                                k: 'sendPersonalGalleryLink',
                                                l: 'Personal gallery link',
                                            },
                                            {
                                                k: 'sendGalleryLink',
                                                l: 'Gallery link',
                                            },
                                            {
                                                k: 'moderationEnabled',
                                                l: 'Moderation',
                                            },
                                        ].map((c) => (
                                            <button
                                                key={c.k}
                                                type="button"
                                                onClick={() =>
                                                    setCrowdForm(
                                                        (f: any) => ({
                                                            ...f,
                                                            [c.k]: !f[c.k],
                                                        }),
                                                    )
                                                }
                                                className={
                                                    'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                                    (crowdForm[c.k]
                                                        ? 'border border-transparent bg-brand-soft text-brand-ink'
                                                        : 'border border-border bg-transparent text-muted-foreground')
                                                }
                                            >
                                                {c.l}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </section>
                    )}

                    {/* ftp sync — separate save */}
                    {ftpForm && (
                        <section className="border-t border-line py-6">
                            <div className="mb-[15px] flex h-7 items-center justify-between">
                                <h3 className="ps-label">FTP sync</h3>
                                <span className="flex items-center gap-3">
                                    {sectionError === 'ftp' && (
                                        <span className="text-[11.5px] font-semibold text-attention">
                                            Save failed
                                        </span>
                                    )}
                                    {ftpDirty && (
                                        <button
                                            type="button"
                                            onClick={saveFtp}
                                            disabled={ftpSaving}
                                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                                        >
                                            {ftpSaving
                                                ? 'Saving…'
                                                : 'Save changes'}
                                        </button>
                                    )}
                                </span>
                            </div>
                            <div className="grid gap-x-[22px] md:grid-cols-2">
                                {[
                                    { k: 'ftpHost', l: 'Host' },
                                    { k: 'ftpPort', l: 'Port' },
                                    { k: 'ftpUser', l: 'Username' },
                                    { k: 'ftpDirectory', l: 'Directory' },
                                ].map((f2) => (
                                    <div key={f2.k} className="mt-[15px] first:mt-0 md:[&:nth-child(2)]:mt-0">
                                        <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                            {f2.l}
                                        </label>
                                        <input
                                            className="ps-input w-full font-mono text-[13px]"
                                            value={ftpForm[f2.k]}
                                            onChange={(e) =>
                                                setFtpForm((f: any) => ({
                                                    ...f,
                                                    [f2.k]: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="mt-[15px]">
                                <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                    Password{' '}
                                    <span className="font-normal text-faint">
                                        — enter to change
                                    </span>
                                </label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    className="ps-input w-full font-mono text-[13px]"
                                    value={ftpForm.ftpPassword}
                                    onChange={(e) =>
                                        setFtpForm((f: any) => ({
                                            ...f,
                                            ftpPassword: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </section>
                    )}

                    {/* slideshows — tag-scoped, separate save per row */}
                    {gallery && (
                        <section className="border-t border-line py-6">
                            <div className="mb-[15px] flex h-7 items-center justify-between">
                                <h3 className="ps-label">Slideshows</h3>
                                <span className="flex items-center gap-3">
                                    {sectionError === 'slideshows' && (
                                        <span className="text-[11.5px] font-semibold text-attention">
                                            Save failed
                                        </span>
                                    )}
                                    {ssEditing == null && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                startSlideshowEdit()
                                            }
                                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                                        >
                                            + New slideshow
                                        </button>
                                    )}
                                </span>
                            </div>
                            <p className="mb-3 max-w-[46ch] text-[12.5px] text-muted-foreground">
                                Named slideshow views scoped to a tag set —
                                tag photos in Media, then point a screen at
                                a slideshow's link.
                            </p>
                            {slideshows.length === 0 && ssEditing == null && (
                                <p className="text-[12.5px] text-faint">
                                    No slideshows yet — the default
                                    slideshow link shows every photo.
                                </p>
                            )}
                            {slideshows.map((s, si) => {
                                const href = publicBase
                                    ? `${publicBase}/slideshow/${s.id}`
                                    : '';
                                return (
                                    <div
                                        key={s.id}
                                        className={
                                            'flex items-center gap-2.5 py-[9px] ' +
                                            (si > 0
                                                ? 'border-t border-line'
                                                : '')
                                        }
                                    >
                                        <div className="min-w-0">
                                            <div className="text-[12.5px] font-semibold leading-tight">
                                                {s.name}
                                                {s.tags.length > 1 && (
                                                    <span className="ml-2 rounded-full bg-secondary px-2 py-[1px] text-[10px] font-semibold text-muted-foreground">
                                                        {s.matchMode === 'all'
                                                            ? 'all tags'
                                                            : 'any tag'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                                {s.tags.length === 0 ? (
                                                    <span className="text-[11px] text-faint">
                                                        No tags — shows every
                                                        photo
                                                    </span>
                                                ) : (
                                                    s.tags.map((t) => (
                                                        <span
                                                            key={t}
                                                            className="rounded-full border border-border px-2 py-[1px] text-[10.5px] font-semibold text-muted-foreground"
                                                        >
                                                            {t}
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                        <div className="ml-auto flex items-center gap-0.5">
                                            {href && (
                                                <>
                                                    <button
                                                        type="button"
                                                        title={
                                                            copied ===
                                                            `ss-${s.id}`
                                                                ? 'Copied'
                                                                : 'Copy link'
                                                        }
                                                        onClick={() =>
                                                            copy(
                                                                `ss-${s.id}`,
                                                                href,
                                                            )
                                                        }
                                                        className={
                                                            'flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent hover:bg-secondary hover:text-foreground ' +
                                                            (copied ===
                                                            `ss-${s.id}`
                                                                ? 'text-good'
                                                                : 'text-faint')
                                                        }
                                                    >
                                                        <IconCopy />
                                                    </button>
                                                    <a
                                                        href={href}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="Open"
                                                        className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-faint hover:bg-secondary hover:text-foreground"
                                                    >
                                                        <IconExternal className="h-[13px] w-[13px]" />
                                                    </a>
                                                </>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    startSlideshowEdit(s)
                                                }
                                                className="ml-1.5 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-brand-ink"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeSlideshow(s)
                                                }
                                                className="ml-2 cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-faint hover:text-attention"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            {ssEditing != null && ssForm && (
                                <div className="mt-3 rounded-[13px] border border-border p-3.5">
                                    <div className="mb-[13px]">
                                        <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                            Name
                                        </label>
                                        <input
                                            autoFocus
                                            className="ps-input w-full"
                                            placeholder="e.g. Team A screen"
                                            value={ssForm.name}
                                            onChange={(e) =>
                                                setSsForm({
                                                    ...ssForm,
                                                    name: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                    <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                        Tags{' '}
                                        <span className="font-normal text-faint">
                                            — empty shows every photo
                                        </span>
                                    </label>
                                    <div className="mb-2 flex flex-wrap items-center gap-1.5">
                                        {ssForm.tags.map((t) => (
                                            <span
                                                key={t}
                                                className="inline-flex items-center gap-1 rounded-full bg-brand-soft py-[3px] pl-2.5 pr-1 text-[12px] font-semibold text-brand-ink"
                                            >
                                                {t}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setSsForm({
                                                            ...ssForm,
                                                            tags: ssForm.tags.filter(
                                                                (x) =>
                                                                    x !== t,
                                                            ),
                                                        })
                                                    }
                                                    className="cursor-pointer border-0 bg-transparent px-1 text-faint hover:text-attention"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                        <input
                                            className="ps-input w-[140px] text-[12.5px]"
                                            placeholder="Type a tag, Enter"
                                            value={ssDraft}
                                            onChange={(e) =>
                                                setSsDraft(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    pushSsTag();
                                                }
                                            }}
                                        />
                                    </div>
                                    {activationTags.filter(
                                        (t) => !ssForm.tags.includes(t.tag),
                                    ).length > 0 && (
                                        <div className="mb-3 flex flex-wrap items-center gap-1">
                                            {activationTags
                                                .filter(
                                                    (t) =>
                                                        !ssForm.tags.includes(
                                                            t.tag,
                                                        ),
                                                )
                                                .slice(0, 12)
                                                .map((t) => (
                                                    <button
                                                        key={t.tag}
                                                        type="button"
                                                        onClick={() =>
                                                            setSsForm({
                                                                ...ssForm,
                                                                tags: [
                                                                    ...ssForm.tags,
                                                                    t.tag,
                                                                ],
                                                            })
                                                        }
                                                        className="cursor-pointer rounded-full border border-dashed border-border bg-transparent px-2.5 py-[3px] font-[inherit] text-[11.5px] text-muted-foreground hover:text-brand-ink"
                                                    >
                                                        + {t.tag}
                                                        <span className="ml-1 tabular-nums text-faint">
                                                            {t.count}
                                                        </span>
                                                    </button>
                                                ))}
                                        </div>
                                    )}
                                    {ssForm.tags.length > 1 && (
                                        <div className="mb-3">
                                            <label className="mb-[7px] block text-[12px] font-semibold text-muted-foreground">
                                                Show photos matching
                                            </label>
                                            <div className="inline-flex gap-0.5 rounded-[9px] bg-secondary p-[3px]">
                                                {[
                                                    {
                                                        l: 'Any tag',
                                                        v: 'any' as const,
                                                    },
                                                    {
                                                        l: 'All tags',
                                                        v: 'all' as const,
                                                    },
                                                ].map((o) => (
                                                    <button
                                                        key={o.v}
                                                        type="button"
                                                        onClick={() =>
                                                            setSsForm({
                                                                ...ssForm,
                                                                matchMode:
                                                                    o.v,
                                                            })
                                                        }
                                                        className={
                                                            'cursor-pointer rounded-[7px] border-0 px-3 py-1 font-[inherit] text-[12px] font-semibold ' +
                                                            (ssForm.matchMode ===
                                                            o.v
                                                                ? 'bg-background text-foreground'
                                                                : 'bg-transparent text-muted-foreground')
                                                        }
                                                    >
                                                        {o.l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            disabled={
                                                ssBusy ||
                                                !ssForm.name.trim()
                                            }
                                            onClick={saveSlideshow}
                                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-white disabled:opacity-50"
                                        >
                                            {ssBusy
                                                ? 'Saving…'
                                                : ssEditing
                                                ? 'Save slideshow'
                                                : 'Create slideshow'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={closeSlideshowEdit}
                                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-faint"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </div>
            {assetPicker && (
                <AssetPicker
                    onClose={() => setAssetPicker(null)}
                    onSelect={(url) => {
                        set(assetPicker, url);
                        setAssetPicker(null);
                    }}
                />
            )}
        </main>
    );
}
